<template>
  <div class="dashboard">
    <!-- Header -->
    <header class="header">
      <div class="container flex justify-between items-center">
        <div class="header-left flex items-center gap-4">
          <!-- Mobile hamburger menu -->
          <button 
            @click="showMobileSidebar = true" 
            class="hamburger-btn"
            aria-label="Open menu"
          >
            <span></span>
            <span></span>
            <span></span>
          </button>
          
          <h1 class="logo">{{ $t('app.name') }}</h1>
        </div>
        
        <div class="header-actions flex items-center gap-4">
          <div class="search-container">
            <div class="search-input-wrapper">
              <input
                v-model="searchQuery"
                @input="handleSearch"
                @blur="closeSuggestions"
                @focus="handleFocus"
                type="text"
                :placeholder="$t('search.placeholder')"
                class="search-input"
              />
              <button 
                v-if="searchQuery.trim()"
                @click="clearSearch"
                class="search-clear-btn"
                :title="$t('search.clear')"
                aria-label="Clear search"
              >
                <svg width="16" height="16" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z"/>
                </svg>
              </button>
            </div>
            <div v-if="searchSuggestions.length > 0" class="search-suggestions">
              <div class="suggestions-header">{{ $t('search.suggestions') }}</div>
              <div
                v-for="suggestion in searchSuggestions"
                :key="suggestion.name"
                @click="selectTag(suggestion.name)"
                class="suggestion-item"
              >
                {{ suggestion.name }} ({{ suggestion.count }})
              </div>
            </div>
          </div>
          
          <!-- Desktop actions (hidden on mobile) -->
          <div class="desktop-actions">
            <template v-if="isLoggedIn">
              <button @click="showUploadModal = true" class="btn btn-primary modern-btn">
                <svg width="20" height="20" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z"/>
                </svg>
                <span>{{ $t('upload.title') }}</span>
              </button>
              <LanguageSwitcher variant="light" />
              <button @click="logout" class="btn btn-secondary modern-btn">
                <svg width="20" height="20" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17,7L15.59,8.41L18.17,11H8V13H18.17L15.59,15.59L17,17L22,12L17,7M4,5H12V3H4A2,2 0 0,0 2,5V19A2,2 0 0,0 4,21H12V19H4V5Z"/>
                </svg>
                <span>{{ $t('nav.logout') }}</span>
              </button>
            </template>
            <template v-else>
              <LanguageSwitcher variant="light" />
              <button @click="$router.push('/login')" class="btn btn-secondary modern-btn">
                <svg width="20" height="20" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M10,17V14H3V10H10V7L15,12L10,17M10,2H19A2,2 0 0,1 21,4V20A2,2 0 0,1 19,22H10A2,2 0 0,1 8,20V18H10V20H19V4H10V6H8V4A2,2 0 0,1 10,2Z"/>
                </svg>
                <span>{{ $t('nav.login') }}</span>
              </button>
            </template>
          </div>
        </div>
      </div>
    </header>

    <!-- Main Content -->
    <main class="main-content">
      <!-- Map Section -->
      <div class="map-section">
        <PhotoMap 
          ref="photoMap"
          :photos="filteredPhotos" 
          @photo-selected="selectPhoto"
        />
      </div>
    </main>

    <!-- Upload Modal -->
    <UploadModal 
      v-if="showUploadModal"
      @close="showUploadModal = false"
      @upload-success="handleUploadSuccess"
    />

    <!-- Mobile Sidebar -->
    <MobileSidebar
      :is-open="showMobileSidebar"
      :is-logged-in="isLoggedIn"
      @close="showMobileSidebar = false"
      @upload="showUploadModal = true"
      @logout="logout"
      @login="$router.push('/login')"
    />

    <!-- Photo Detail Modal -->
    <PhotoDetailModal
      ref="photoDetailModal"
      v-if="selectedPhoto"
      :photo="selectedPhoto"
      @close="closePhotoDetail"
      @photo-deleted="handlePhotoDeleted"
      @search-tag="searchByTag"
      @tags-updated="handleTagsUpdated"
      @photos-updated="handlePhotosUpdated"
      @request-upload-to-marker="handleUploadToMarkerRequest"
    />

    <!-- Marker Upload Modal -->
    <MarkerUploadModal
      v-if="showMarkerUploadModal"
      :marker-group-id="selectedMarkerGroupId"
      :default-tags="selectedMarkerTags"
      @close="closeMarkerUploadModal"
      @upload-success="handleMarkerUploadSuccess"
    />
  </div>
</template>

<script>
import { photosAPI } from '../services/api'
import PhotoMap from '../components/PhotoMap.vue'
import UploadModal from '../components/UploadModal.vue'
import PhotoDetailModal from '../components/PhotoDetailModal.vue'
import MarkerUploadModal from '../components/MarkerUploadModal.vue'
import LanguageSwitcher from '../components/LanguageSwitcher.vue'
import MobileSidebar from '../components/MobileSidebar.vue'

export default {
  name: 'Dashboard',
  components: {
    PhotoMap,
    UploadModal,
    PhotoDetailModal,
    MarkerUploadModal,
    LanguageSwitcher,
    MobileSidebar
  },
  computed: {
    isLoggedIn() {
      return !!localStorage.getItem('token')
    }
  },
  data() {
    return {
      photos: [],
      filteredPhotos: [],
      searchQuery: '',
      searchSuggestions: [],
      showUploadModal: false,
      selectedPhoto: null,
      loading: false,
      searchTimeout: null,
      showMobileSidebar: false,
      showMarkerUploadModal: false,
      selectedMarkerGroupId: null,
      selectedMarkerTags: []
    }
  },
  async mounted() {
    await this.loadPhotos()
  },
  methods: {
    async loadPhotos() {
      this.loading = true
      try {
        const response = await photosAPI.getAll()
        this.photos = response.data
        this.filteredPhotos = this.photos
      } catch (error) {
        console.error('Error loading photos:', error)
      } finally {
        this.loading = false
      }
    },

    handleSearch() {
      // Clear previous timeout
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout)
      }

      // Debounce search
      this.searchTimeout = setTimeout(async () => {
        if (this.searchQuery.trim()) {
          // Get search suggestions
          try {
            const response = await photosAPI.searchTags(this.searchQuery)
            this.searchSuggestions = response.data
          } catch (error) {
            console.error('Error getting search suggestions:', error)
            this.searchSuggestions = []
          }

          // Filter photos by search query
          await this.searchPhotos()
        } else {
          this.searchSuggestions = []
          this.filteredPhotos = this.photos
        }
      }, 300)
    },

    async searchPhotos() {
      if (!this.searchQuery.trim()) {
        this.filteredPhotos = this.photos
        return
      }

      try {
        const response = await photosAPI.search(this.searchQuery)
        this.filteredPhotos = response.data
      } catch (error) {
        console.error('Error searching photos:', error)
        this.filteredPhotos = []
      }
    },

    selectTag(tagName) {
      this.searchQuery = tagName
      this.searchSuggestions = []
      this.searchPhotos()
    },

    searchByTag(tagName) {
      this.searchQuery = tagName
      this.searchSuggestions = []
      this.searchPhotos()
    },

    clearSearch() {
      this.searchQuery = ''
      this.searchSuggestions = []
      this.filteredPhotos = this.photos
    },

    closeSuggestions() {
      // Use setTimeout to allow click events on suggestions to fire first
      setTimeout(() => {
        this.searchSuggestions = []
      }, 150)
    },

    handleFocus() {
      // If there's a search query, show suggestions again when focused
      if (this.searchQuery.trim()) {
        this.handleSearch()
      }
    },

    selectPhoto(photo) {
      // Always use the complete photo data from the original photos array
      // to ensure all tags are shown, not just the filtered ones
      const completePhoto = this.photos.find(p => p.id === photo.id) || photo
      this.selectedPhoto = completePhoto
    },

    closePhotoDetail() {
      this.selectedPhoto = null
    },

    async handleUploadSuccess() {
      this.showUploadModal = false
      await this.loadPhotos()
    },

    logout() {
      localStorage.removeItem('token')
      localStorage.removeItem('user')
      this.$router.push('/login')
    },

    async handlePhotoDeleted(photoId) {
      // Close the modal
      this.selectedPhoto = null
      // Refresh the photos list
      await this.loadPhotos()
    },

    async handleTagsUpdated() {
      await this.loadPhotos()
    },

    async handlePhotosUpdated() {
      await this.loadPhotos()
    },

    async handleUploadToMarkerRequest(data) {
      this.selectedMarkerGroupId = data.markerGroupId
      this.selectedMarkerTags = data.tags
      this.showMarkerUploadModal = true
    },

    closeMarkerUploadModal() {
      this.showMarkerUploadModal = false
      this.selectedMarkerGroupId = null
      this.selectedMarkerTags = []
    },

    async handleMarkerUploadSuccess() {
      this.showMarkerUploadModal = false
      await this.loadPhotos()
      
      // Refresh the PhotoDetailModal carousel if it's open
      if (this.selectedPhoto && this.$refs.photoDetailModal) {
        await this.$refs.photoDetailModal.loadMarkerPhotos()
      }
    },

    togglePhotoView() {
      // This method is no longer needed as showAllPhotos is removed.
      // Keeping it for now in case it's called from elsewhere, but it will do nothing.
      console.warn("togglePhotoView is no longer functional as showAllPhotos is removed.")
    }
  }
}
</script>

<style scoped>
.dashboard {
  min-height: 100vh;
  background: linear-gradient(135deg, #0f0c29 0%, #1a1a2e 40%, #16213e 100%);
}

.header {
  background: rgba(10, 10, 25, 0.85);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.07);
  padding: 1rem 0;
  box-shadow: 0 2px 24px rgba(0, 0, 0, 0.4);
  position: sticky;
  top: 0;
  z-index: 5000;
}

.logo {
  font-size: 1.8rem;
  font-weight: 700;
  margin: 0;
  background: linear-gradient(90deg, #00c6ff, #0072ff);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.hamburger-btn {
  display: none;
  flex-direction: column;
  gap: 3px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px;
  border-radius: 6px;
  transition: all 0.2s ease;
}

.hamburger-btn:hover {
  background: rgba(255, 255, 255, 0.08);
}

.hamburger-btn span {
  display: block;
  width: 20px;
  height: 2px;
  background: rgba(255, 255, 255, 0.8);
  transition: all 0.2s ease;
  border-radius: 1px;
}

.hamburger-btn:hover span {
  background: #00c6ff;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.desktop-actions {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.search-container {
  position: relative;
}

.search-input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.search-input {
  width: 300px;
  padding: 10px 16px;
  padding-right: 40px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 25px;
  font-size: 14px;
  background: rgba(255, 255, 255, 0.07);
  color: #f1f5f9;
  transition: all 0.3s ease;
}

.search-input::placeholder {
  color: rgba(255, 255, 255, 0.4);
}

.search-input:focus {
  outline: none;
  border-color: #00c6ff;
  box-shadow: 0 0 0 3px rgba(0, 198, 255, 0.15);
  background: rgba(255, 255, 255, 0.1);
}

.search-clear-btn {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  padding: 6px;
  border-radius: 50%;
  color: rgba(255, 255, 255, 0.4);
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.search-clear-btn:hover {
  background: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.9);
  transform: translateY(-50%) scale(1.1);
}

.search-suggestions {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: rgba(15, 12, 41, 0.97);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5);
  z-index: 9999;
  max-height: 200px;
  overflow-y: auto;
  margin-top: 4px;
}

.suggestions-header {
  padding: 8px 16px;
  font-size: 0.75rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.4);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  background: rgba(0, 198, 255, 0.05);
  border-bottom: 1px solid rgba(255, 255, 255, 0.07);
}

.suggestion-item {
  padding: 12px 16px;
  cursor: pointer;
  color: rgba(255, 255, 255, 0.85);
  transition: background-color 0.2s ease;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}

.suggestion-item:hover {
  background-color: rgba(0, 198, 255, 0.1);
}

.suggestion-item:last-child {
  border-bottom: none;
}

.main-content {
  padding: 2rem;
  height: calc(100vh - 80px); /* Full height minus header height */
}

.map-section {
  height: 100%;
  width: 100%;
  border-radius: 20px;
  overflow: hidden;
  background: white;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
}

.content-grid {
  display: grid;
  gap: 2rem;
}

/* Modern Button Styles */
.btn {
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 12px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  font-family: inherit;
  min-height: 44px;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  justify-content: center;
  position: relative;
  overflow: hidden;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none !important;
}

.btn-primary {
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
  color: white;
  border: 1px solid transparent;
  box-shadow: 0 4px 6px -1px rgba(99, 102, 241, 0.3);
}

.btn-primary:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(99, 102, 241, 0.4);
}

.btn-secondary {
  background: rgba(255, 255, 255, 0.07);
  color: rgba(255, 255, 255, 0.75);
  border: 1px solid rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(10px);
}

.btn-secondary:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.12);
  color: #fff;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
}

.modern-btn {
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.15);
}

.modern-btn svg {
  flex-shrink: 0;
}

.modern-btn span {
  white-space: nowrap;
}

@media (max-width: 1024px) {
  .hamburger-btn {
    display: flex;
  }
  
  .desktop-actions {
    display: none;
  }
  
  .header-actions {
    flex-direction: row;
    gap: 1rem;
  }

  .search-input {
    width: 240px;
  }
}

@media (max-width: 768px) {
  .main-content {
    padding: 12px;
    height: calc(100vh - 80px); /* Adjust for header height */
  }
  
  .map-section {
    width: 100%;
    /* Portrait orientation: height > width */
    height: 70vh;
  }
  
  .header {
    padding: 0.75rem 0;
  }
  
  .container {
    padding: 0 1rem;
  }

  .search-input {
    width: 200px;
  }
}

/* Mobile landscape orientation: width > height */
@media (max-width: 768px) and (orientation: landscape) {
  .map-section {
    height: 60vw;
  }
}

@media (max-width: 480px) {
  .search-input {
    width: 180px;
    font-size: 14px;
  }
  
  .logo {
    font-size: 1.5rem;
  }
  
  .header-left {
    gap: 0.5rem;
  }
  
  .main-content {
    padding: 12px;
  }
  
  .map-section {
    /* Portrait orientation: height > width */
    height: 70vh;
  }
}

/* Small mobile landscape orientation: width > height */
@media (max-width: 480px) and (orientation: landscape) {
  .map-section {
    height: 60vw;
  }
}

/* Dark mode support */
@media (prefers-color-scheme: dark) {
  .dashboard {
    background: linear-gradient(135deg, #0f0c29 0%, #1a1a2e 40%, #16213e 100%);
  }

  .header {
    background: rgba(10, 10, 20, 0.95);
    border-bottom: 1px solid rgba(255, 255, 255, 0.07);
    box-shadow: 0 2px 20px rgba(0, 0, 0, 0.4);
  }

  .logo {
    background: linear-gradient(90deg, #00c6ff, #0072ff);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }

  .hamburger-btn:hover {
    background: rgba(99, 102, 241, 0.2);
  }

  .hamburger-btn span {
    background: #818cf8;
  }

  .hamburger-btn:hover span {
    background: #6366f1;
  }

  .search-input {
    background: rgba(17, 24, 39, 0.8);
    border-color: rgba(255, 255, 255, 0.2);
    color: #f9fafb;
  }

  .search-input::placeholder {
    color: #9ca3af;
  }

  .search-input:focus {
    background: rgba(17, 24, 39, 0.95);
    border-color: #6366f1;
    box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.2);
    color: #f9fafb;
  }

  .search-suggestions {
    background: rgba(17, 24, 39, 0.95);
    border-color: rgba(255, 255, 255, 0.1);
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
  }

  .suggestions-header {
    background: rgba(99, 102, 241, 0.2);
    color: #9ca3af;
    border-bottom-color: rgba(255, 255, 255, 0.1);
  }

  .suggestion-item {
    color: #f3f4f6;
    border-bottom-color: rgba(255, 255, 255, 0.1);
  }

  .suggestion-item:hover {
    background-color: rgba(99, 102, 241, 0.2);
  }

  .map-section {
    background: rgba(17, 24, 39, 0.8);
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  }

  .btn-secondary {
    background: rgba(255, 255, 255, 0.07);
    color: rgba(255, 255, 255, 0.75);
    border-color: rgba(255, 255, 255, 0.15);
  }

  .btn-secondary:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.12);
    color: #fff;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  }

  .modern-btn {
    border-color: rgba(255, 255, 255, 0.1);
  }
}
</style> 